// src/config/palette.config.ts — SCE Issues
// Palette des couleurs métier (Lot 8, demande 12) : les teintes proposées aux
// coordinateurs, et les règles de lisibilité qui vont avec.
//
// ─── D'où viennent ces teintes ───
//
// Table 4 « Tertiary Colors » du document Sartorius « Color Codes – Overview »
// (cbd-overview-colorcodes-en-sartorius, reçu le 25/09/2026). Pas de sélecteur
// libre ni de hex saisi à la main : un coordinateur choisit DANS cette liste.
// Deux teintes n'ont pas de nom dans le document (#462882, #F59100) : on les
// appelle « Indigo » et « Orange ».
//
// ─── Les teintes écartées, et pourquoi ───
//
// La couleur d'un métier côtoie partout les badges de criticité et de statut,
// qui parlent en Traffic Light (Table 5). Une teinte qui s'y confond ferait
// mentir la fiche :
//  - Deep Red #B40037 et Magenta #E6007D → se lisent « Critique » (rouge) ;
//  - Dark Green #1E5F1E → se lit « Clos » / « Mineur » (vert).
// Le jaune (interaction) et les gris (désactivé, secondaire) sont exclus pour
// la même raison.
//
// ─── Couleur du texte posé dessus ───
//
// La charte indique « Font: White » sur toute la Table 4. Or le blanc est
// illisible sur les teintes claires : 2,3:1 sur Orange, 2,4:1 sur Teal, 2,9:1
// sur Light Blue, quand le minimum lisible est 4,5:1. Arbitrage du 25/09 : le
// texte est noir ou blanc selon la teinte, au meilleur contraste (`texteSur`).
//
// ⚠️ Des hex ici, et seulement ici (avec globals.css) : ce sont les VALEURS
//    que prennent les variables `--color-metier-*` au runtime. Les composants,
//    eux, ne lisent jamais que `var(--color-metier-*)`.

import type { Metier } from '@/types/issue.types'

export interface TeinteMetier {
  nom: string
  /** Majuscules, `#RRGGBB` : c'est la forme stockée et comparée. */
  hex: string
}

/** Ordre de la Table 4, teintes écartées retirées. */
export const PALETTE_METIER: readonly TeinteMetier[] = [
  { nom: 'Heather Violet', hex: '#F06EA5' },
  { nom: 'Purple', hex: '#961982' },
  { nom: 'Dark Purple', hex: '#690F5F' },
  { nom: 'Orchid', hex: '#7C5AD7' },
  { nom: 'Indigo', hex: '#462882' },
  { nom: 'Orange', hex: '#F59100' },
  { nom: 'Brown', hex: '#AF5523' },
  { nom: 'Light Blue', hex: '#00A0E6' },
  { nom: 'Turquoise', hex: '#008796' },
  { nom: 'Teal', hex: '#00B9BE' },
  { nom: 'Signal Blue', hex: '#0069BE' },
]

/**
 * Couleurs d'origine — celles de globals.css, qui s'appliquent tant que
 * personne n'a rien changé. ⚠️ À garder identiques aux valeurs de
 * `--color-metier-*` dans globals.css (premier affichage, avant tout script).
 */
export const COULEURS_METIER_DEFAUT: Readonly<Record<Metier, string>> = {
  ELEC: '#00A0E6',   // Light Blue
  AUTOM: '#7C5AD7',  // Orchid
  MECA: '#F59100',   // Orange
  PROCESS: '#00B9BE', // Teal
}

/** La teinte de la palette, quelle que soit la casse du hex — ou undefined. */
export function teinteParHex(hex: string): TeinteMetier | undefined {
  const cle = hex.trim().toUpperCase()
  return PALETTE_METIER.find((t) => t.hex === cle)
}

// ─── Contraste (WCAG 2) ───

function luminance(hex: string): number {
  const canaux = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
  const [r = 0, v = 0, b = 0] = canaux.map((c) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4,
  )
  return 0.2126 * r + 0.7152 * v + 0.0722 * b
}

/** Rapport de contraste entre deux couleurs `#RRGGBB` (1 à 21). */
export function contraste(a: string, b: string): number {
  const [clair, fonce] = [luminance(a), luminance(b)].sort((x, y) => y - x)
  return ((clair ?? 0) + 0.05) / ((fonce ?? 0) + 0.05)
}

/** Seuil AA pour un texte courant — celui d'un badge. */
export const CONTRASTE_TEXTE_MIN = 4.5

/**
 * Couleur du texte posé sur une teinte : blanc (la charte) dès qu'il est
 * lisible, noir sinon. Renvoie un jeton CSS, pas un hex.
 */
export function texteSur(hex: string): 'var(--color-white)' | 'var(--color-black)' {
  return contraste(hex, '#FFFFFF') >= CONTRASTE_TEXTE_MIN
    ? 'var(--color-white)'
    : 'var(--color-black)'
}

// ─── Teintes trop proches ───
//
// Deux métiers ne peuvent pas avoir la MÊME teinte (refusé). Deux teintes
// voisines, elles, restent permises mais sont signalées : on les confond d'un
// coup d'œil, et plus encore avec une vision des couleurs atypique.
//
// Liste explicite plutôt qu'un seuil de distance calculé : la distance
// perceptive (ΔE) ne signale pas Light Blue / Teal, justement la paire
// ELEC / PROCESS d'origine, que l'on confond pourtant en atelier. Les autres
// paires sont celles dont le ΔE est inférieur à 30.

const PAIRES_PROCHES: readonly (readonly [string, string])[] = [
  ['#961982', '#690F5F'], // Purple / Dark Purple
  ['#008796', '#00B9BE'], // Turquoise / Teal
  ['#690F5F', '#462882'], // Dark Purple / Indigo
  ['#00A0E6', '#0069BE'], // Light Blue / Signal Blue
  ['#7C5AD7', '#462882'], // Orchid / Indigo
  ['#00A0E6', '#00B9BE'], // Light Blue / Teal
  ['#00A0E6', '#008796'], // Light Blue / Turquoise
]

export function sontProches(a: string, b: string): boolean {
  const x = a.toUpperCase()
  const y = b.toUpperCase()
  return PAIRES_PROCHES.some(([p, q]) => (p === x && q === y) || (p === y && q === x))
}
